/**
 * SkyRenderer - Complete sky rendering and effects system
 *
 * Handles:
 * - Sky gradient background with sun and atmospheric effects
 * - Multi-layer parallax clouds with chunk-based generation
 * - Shooting stars
 * - Lightning bolts
 * - Cloud collision and bouncing
 */

export class SkyRenderer {
  constructor() {
    // Cloud system
    this.cloudChunkSize = 600;
    this.cloudLayers = [
      { speed: 0.2, alpha: 0.35, density: 1/1700, size: [0.7, 1.3] },
      { speed: 0.35, alpha: 0.5, density: 1/1350, size: [0.65, 1.1] },
      { speed: 0.55, alpha: 0.65, density: 1/1150, size: [0.6, 1.0] },
    ];
    this.cloudsByLayer = this.cloudLayers.map(() => new Map()); // Map<chunkIndex, Cloud[]>

    // Cloud images
    this.cloudImgSrcs = [
      'img_assets/cloud1.png',
      'img_assets/cloud2.png',
      'img_assets/cloud3.png',
      'img_assets/cloud4.png',
    ];
    this.cloudImgs = this.cloudImgSrcs.map(src => {
      const im = new Image();
      im.src = src;
      im.onerror = () => console.error(`Failed to load cloud image: ${src}`);
      return im;
    });
    this.CLOUD_IMG_BASE_SCALE = 0.13;
    this.CLOUD_PAGE_JITTER_X = 12; // px
    this.CLOUD_PAGE_JITTER_Y = 12; // px
    this.CLOUD_PAGE_SCALE_VAR = 0.15; // +/- 15%
    this.cloudImgBounds = new Array(this.cloudImgSrcs.length).fill(null);

    // Cloud drift (idle movement)
    this.cloudDrift = 0;
    this.CLOUD_IDLE_SPEED = 64; // pixels per second
    this.CLOUD_IDLE_FACTOR = 0.6; // per-layer multiplier

    // Cloud camera
    this.cloudCamY = 0;

    // Cloud collision
    this.cloudBounceCooldown = 0;
    this.cloudBounceArmed = false;

    // Shooting stars
    this.shootingStars = []; // { x,y,vx,vy,life,dur }
    this.shootingTimer = 0;
    this.shootingNext = 8 + Math.random() * 12;

    // Lightning
    this.lightning = []; // { x, y, life, dur, seed }
    this.lightningTimer = 0;
    this.lightningNext = 5 + Math.random() * 8;

    // Sky darkness scale (higher = more gradual transition)
    this.SKY_DARKNESS_SCALE = 25;

    // Sky noise pattern (for subtle texture)
    this.skyNoisePattern = null;

    // Initialize
    this.initSkyNoise();
    this.initCloudImages();
  }

  /**
   * Initialize sky noise pattern for subtle texture
   */
  initSkyNoise() {
    const S = 196;
    const cv = document.createElement('canvas');
    cv.width = S;
    cv.height = S;
    const c = cv.getContext('2d');
    const img = c.createImageData(S, S);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = Math.floor(Math.random() * 12);
      img.data[i] = v;
      img.data[i + 1] = v;
      img.data[i + 2] = v;
      img.data[i + 3] = 160; // softer per-texel alpha
    }
    c.putImageData(img, 0, 0);
    this.skyNoisePattern = c.createPattern(cv, 'repeat');
  }

  /**
   * Initialize cloud images and compute bounds
   */
  initCloudImages() {
    // Wait for images to load then compute bounds
    this.cloudImgs.forEach((img, i) => {
      if (img.complete) {
        this.computeCloudBoundsForIndex(i);
      } else {
        img.onload = () => this.computeCloudBoundsForIndex(i);
      }
    });
  }

  /**
   * Compute opaque bounds for a cloud image (for tighter hitboxes)
   */
  computeCloudBoundsForIndex(i) {
    const img = this.cloudImgs[i];
    if (!img || !img.naturalWidth) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const off = document.createElement('canvas');
    off.width = w;
    off.height = h;
    const octx = off.getContext('2d');
    octx.clearRect(0, 0, w, h);
    octx.drawImage(img, 0, 0);
    const data = octx.getImageData(0, 0, w, h).data;
    let minX = w, minY = h, maxX = -1, maxY = -1;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const a = data[(y * w + x) * 4 + 3];
        if (a > 8) { // treat low alpha as transparent
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) { // fully transparent fallback
      this.cloudImgBounds[i] = { left: w / 2, right: w / 2, top: h / 2, bottom: h / 2 };
    } else {
      this.cloudImgBounds[i] = { left: minX, right: maxX, top: minY, bottom: maxY };
    }
  }

  /**
   * Seeded random number generator
   */
  seededRandom(seed) {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  }

  /**
   * Generate clouds for a chunk
   */
  generateCloudsForChunk(layerIdx, chunkIdx, pageY) {
    const layer = this.cloudLayers[layerIdx];
    const layerSeed = layerIdx * 999 + chunkIdx * 12345 + pageY * 54321;
    const chunkX = chunkIdx * this.cloudChunkSize;
    const chunkW = this.cloudChunkSize;

    const pageSeed = layerSeed + pageY * 7919;
    const jitterX = (this.seededRandom(pageSeed * 2.1) - 0.5) * 2 * this.CLOUD_PAGE_JITTER_X;
    const jitterY = (this.seededRandom(pageSeed * 3.7) - 0.5) * 2 * this.CLOUD_PAGE_JITTER_Y;
    const scaleVar = (this.seededRandom(pageSeed * 5.3) - 0.5) * 2 * this.CLOUD_PAGE_SCALE_VAR;

    // Skip underground pages entirely (pageY > 0 means below ground)
    if (pageY > 0) return [];

    // Increase cloud density at higher altitudes (more clouds as you go up)
    // pageY is negative for altitude (e.g., -1, -2, -3...)
    // Boost pageY=0 and early pages more, then taper off at very high altitudes
    let altitudeFactor = 1.0;
    if (pageY === 0) {
      altitudeFactor = 1.3; // slight boost at ground level for reachability
    } else if (pageY < 0) {
      // Moderate boost higher up, cap at 2x
      const absPage = Math.abs(pageY);
      if (absPage <= 2) {
        altitudeFactor = 1.3 + absPage * 0.25; // pageY=-1: 1.55x, pageY=-2: 1.8x
      } else {
        altitudeFactor = Math.min(2.0, 1.8 + (absPage - 2) * 0.1); // gradual increase, cap at 2x
      }
    }
    const count = Math.max(1, Math.round(chunkW * layer.density * altitudeFactor));

    // Debug logging disabled for production
    // if (pageY <= 0 && pageY >= -5 && chunkIdx === 0 && layerIdx === 0) {
    //   console.log(`[CLOUD GEN] pageY=${pageY}, altitudeFactor=${altitudeFactor.toFixed(2)}x, generating ${count} clouds (base would be ${Math.round(chunkW * layer.density)})`);
    // }

    const clouds = [];

    for (let i = 0; i < count; i++) {
      const seed = layerSeed + i * 7;
      const rx = this.seededRandom(seed);
      const ry = this.seededRandom(seed + 1);
      const rs = this.seededRandom(seed + 2);
      const ri = this.seededRandom(seed + 3);

      const x = chunkX + rx * chunkW + jitterX;
      // Increase vertical spread to cover the full 800px page height (prevent dead zones)
      const y = pageY * 800 + (ry - 0.5) * 800 + jitterY;

      // For pageY=0, only allow clouds high enough to require double jump or bounce
      // Single jump reaches ~60-80px, double jump ~120-150px
      // Allow clouds starting at ~80px height (requires double jump or spider bounce)
      if (pageY === 0 && y > 320) continue; // Skip clouds in bottom 320px, allowing them from ~80px+ above ground

      const sizeMin = layer.size[0];
      const sizeMax = layer.size[1];
      const scale = (sizeMin + rs * (sizeMax - sizeMin)) * (1 + scaleVar);

      const imgIndex = Math.floor(ri * this.cloudImgSrcs.length);

      clouds.push({ x, y, scale, imgIndex });
    }

    return clouds;
  }

  /**
   * Clear cloud cache (useful when regenerating with new parameters)
   */
  clearCloudCache() {
    this.cloudsByLayer.forEach(map => map.clear());
  }

  /**
   * Get or generate clouds for a chunk
   */
  getCloudsForChunk(layerIdx, chunkIdx, pageY) {
    const layerMap = this.cloudsByLayer[layerIdx];
    const key = `${chunkIdx},${pageY}`;

    if (!layerMap.has(key)) {
      const clouds = this.generateCloudsForChunk(layerIdx, chunkIdx, pageY);
      layerMap.set(key, clouds);
    }

    return layerMap.get(key);
  }

  /**
   * Update shooting stars
   */
  updateShootingStars(dt, w, h, playerHeightAboveGround = 0) {
    // Only trigger shooting stars when the player is truly high in the sky
    const MIN_HEIGHT_FOR_STARS = Math.max(h * 1.1, 900); // require very high altitude

    this.shootingTimer += dt;
    if (this.shootingTimer >= this.shootingNext && playerHeightAboveGround >= MIN_HEIGHT_FOR_STARS) {
      this.shootingTimer = 0;
      this.shootingNext = 8 + Math.random() * 12;
      const x = Math.random() * w;
      const y = Math.random() * (h * 0.4);
      const angle = Math.PI * 0.25 + Math.random() * (Math.PI * 0.5);
      const speed = 200 + Math.random() * 300;
      this.shootingStars.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0,
        dur: 0.8 + Math.random() * 0.8
      });
    }

    for (const s of this.shootingStars) {
      s.life += dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
    }

    this.shootingStars = this.shootingStars.filter(s =>
      s.life < s.dur && s.x > -200 && s.x < w + 200 && s.y > -100 && s.y < h + 100
    );
  }

  /**
   * Update lightning
   */
  updateLightning(dt, w, h, playerHeightAboveGround = 0) {
    // Only trigger lightning when the player is very high in the air
    const MIN_HEIGHT_FOR_LIGHTNING = Math.max(h * 1.35, 1200);

    this.lightningTimer += dt;
    if (this.lightningTimer >= this.lightningNext && playerHeightAboveGround >= MIN_HEIGHT_FOR_LIGHTNING) {
      this.lightningTimer = 0;
      this.lightningNext = 5 + Math.random() * 8;
      const y = Math.random() * (h * 0.5);
      const x = Math.random() * w;
      this.lightning.push({ x, y, life: 0, dur: 0.25 + Math.random() * 0.25, seed: (Math.random() * 1e9) | 0 });
    }

    for (const e of this.lightning) {
      e.life += dt;
    }

    this.lightning = this.lightning.filter(e => e.life < e.dur);
  }

  /**
   * Update cloud drift
   */
  updateCloudDrift(dt) {
    this.cloudDrift += this.CLOUD_IDLE_SPEED * dt;
  }

  /**
   * Update all sky systems
   */
  update(dt, w, h, playerHeightAboveGround = 0) {
    this.updateShootingStars(dt, w, h, playerHeightAboveGround);
    // DISABLED: Lightning effects removed
    // this.updateLightning(dt, w, h, playerHeightAboveGround);
    this.updateCloudDrift(dt);

    // Update cloud bounce cooldown
    if (this.cloudBounceCooldown > 0) {
      this.cloudBounceCooldown -= dt;
    }
  }

  /**
   * Calculate sky darkness based on altitude
   */
  skyDarkness(h, playerY) {
    const altitudeScreens = Math.abs(playerY) / h;
    const t = Math.min(1, altitudeScreens / this.SKY_DARKNESS_SCALE);
    // Use quadratic easing for smoother transition
    return t * t;
  }

  /**
   * Draw sky background with gradient, sun, and noise
   */
  drawSkyBackground(ctx, w, h, groundH, cameraY, playerY) {
    const groundTopScreen = (h - groundH) + cameraY;
    const grad = ctx.createLinearGradient(0, 0, 0, groundTopScreen);
    grad.addColorStop(0.0, '#5aa7e6');
    grad.addColorStop(0.5, '#4b8fd0');
    grad.addColorStop(1.0, '#2f5e9a');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // DISABLED: Darken sky progressively with altitude
    const darkK = 0; // FORCED TO ZERO - no darkening
    if (darkK > 0.001) {
      ctx.fillStyle = `rgba(0,0,0,${darkK * 0.7})`;
      ctx.fillRect(0, 0, w, groundTopScreen);
    }

    // Subtle noise texture
    if (this.skyNoisePattern) {
      ctx.save();
      ctx.globalAlpha = 0.012; // slightly softer to avoid banding/rings
      ctx.fillStyle = this.skyNoisePattern;
      ctx.fillRect(0, 0, w, groundTopScreen);
      ctx.restore();
    }
  }

  /**
   * Draw shooting stars
   */
  drawShootingStars(ctx, w, h, groundH, cameraY) {
    if (!this.shootingStars.length) return;
    const groundTopScreen = (h - groundH) + cameraY;
    ctx.save();
    ctx.beginPath();
    for (const s of this.shootingStars) {
      if (s.y >= groundTopScreen - 2) continue;
      const t = s.life / s.dur;
      const fade = t < 0.1 ? t / 0.1 : (t > 0.8 ? (1 - t) / 0.2 : 1);
      const alpha = fade * 0.9;
      const len = 24;
      const angle = Math.atan2(s.vy, s.vx);
      const x1 = s.x;
      const y1 = s.y;
      const x2 = x1 - Math.cos(angle) * len;
      const y2 = y1 - Math.sin(angle) * len;
      const grad = ctx.createLinearGradient(x1, y1, x2, y2);
      grad.addColorStop(0, `rgba(255,255,220,${alpha})`);
      grad.addColorStop(1, 'rgba(255,255,220,0)');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 2;
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
    }
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Draw lightning bolts
   */
  drawLightning(ctx, w, h, groundH, cameraY) {
    if (!this.lightning.length) return;
    const groundTop = (h - groundH) + cameraY;
    ctx.save();
    for (const e of this.lightning) {
      if (e.y >= groundTop - 2) continue; // sky only
      const t = e.life / e.dur;
      const fade = 1 - t;
      ctx.strokeStyle = `rgba(255,255,255,${fade * 0.8})`;
      ctx.lineWidth = 2 + Math.random() * 2;
      ctx.beginPath();
      const segments = 6 + Math.floor(Math.random() * 4);
      let x = e.x;
      let y = e.y;
      ctx.moveTo(x, y);
      for (let i = 0; i < segments; i++) {
        x += (Math.random() - 0.5) * 20;
        y += (groundTop - e.y) / segments;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * Draw clouds
   */
  drawClouds(ctx, w, h, groundH, cameraX, cameraY) {
    const groundTopScreen = (h - groundH) + cameraY;
    ctx.save();
    // Expand clipping area with buffer to prevent clouds disappearing prematurely when ascending
    // Add 200px buffer, but clamp to screen height to prevent showing clouds below ground
    ctx.beginPath();
    const clipHeight = Math.min(h, Math.max(0, groundTopScreen + 200));
    ctx.rect(0, 0, w, clipHeight);
    ctx.clip();

    for (let li = 0; li < this.cloudLayers.length; li++) {
      const layer = this.cloudLayers[li];
      const layerX = cameraX * layer.speed + this.cloudDrift * this.CLOUD_IDLE_FACTOR * layer.speed;
      const layerY = -this.cloudCamY; // cameraY translates world -> screen, so invert here to match draw math

      const leftChunk = Math.floor((layerX - w * 0.5) / this.cloudChunkSize);
      const rightChunk = Math.floor((layerX + w * 1.5) / this.cloudChunkSize);

      const topPage = Math.floor((layerY - h * 0.5) / 800);
      const bottomPage = Math.floor((layerY + h * 1.5) / 800);

      ctx.globalAlpha = layer.alpha * (this.cloudVisibility ?? 1);

      for (let ci = leftChunk; ci <= rightChunk; ci++) {
        for (let pi = topPage; pi <= bottomPage; pi++) {
          const clouds = this.getCloudsForChunk(li, ci, pi);
          for (const c of clouds) {
            const sx = c.x - layerX;
            const sy = c.y - layerY;
            const img = this.cloudImgs[c.imgIndex];
            if (!img || !img.complete || !img.naturalWidth) continue;

            const finalScale = c.scale * this.CLOUD_IMG_BASE_SCALE;
            const dw = img.naturalWidth * finalScale;
            const dh = img.naturalHeight * finalScale;
            ctx.drawImage(img, sx - dw / 2, sy - dh / 2, dw, dh);
          }
        }
      }
    }

    ctx.restore();
  }

  /**
   * Check collision with clouds and handle bouncing
   */
  checkCloudCollision(player, cameraX, cameraY, groundH, viewW, viewH) {
    // Only check if cooldown expired
    if (this.cloudBounceCooldown > 0) return null;

    // Only bounce if armed and in air
    if (!this.cloudBounceArmed || player.onGround) return null;

    const playerRect = {
      x: player.x - cameraX,
      y: player.y + cameraY,
      w: player.w,
      h: player.h
    };
    const groundTopScreen = (viewH - groundH) + cameraY;

    const rectOverlap = (a, b) => !(
      a.x + a.w < b.x ||
      b.x + b.w < a.x ||
      a.y + a.h < b.y ||
      b.y + b.h < a.y
    );

    // Check each layer (from front to back)
    for (let li = this.cloudLayers.length - 1; li >= 0; li--) {
      const layer = this.cloudLayers[li];
      const layerX = cameraX * layer.speed + this.cloudDrift * this.CLOUD_IDLE_FACTOR * layer.speed;
      const layerY = -this.cloudCamY; // use same vertical transform as world elements

      const leftChunk = Math.floor((layerX - viewW * 0.5) / this.cloudChunkSize);
      const rightChunk = Math.floor((layerX + viewW * 1.5) / this.cloudChunkSize);
      const topPage = Math.floor((layerY - viewH * 0.5) / 800);
      const bottomPage = Math.floor((layerY + viewH * 1.5) / 800);

      for (let ci = leftChunk; ci <= rightChunk; ci++) {
        for (let pi = topPage; pi <= bottomPage; pi++) {
          const clouds = this.getCloudsForChunk(li, ci, pi);

          for (const c of clouds) {
            const img = this.cloudImgs[c.imgIndex];
            if (!img || !img.complete || !img.naturalWidth) continue;
            const bounds = this.cloudImgBounds[c.imgIndex];
            if (!bounds) continue;

            const finalScale = c.scale * this.CLOUD_IMG_BASE_SCALE;
            const imgW = img.naturalWidth * finalScale;
            const imgH = img.naturalHeight * finalScale;

            const cloudScreenX = c.x - layerX;
            const cloudScreenY = c.y - layerY;

            if (cloudScreenY + imgH / 2 > groundTopScreen - 2) continue;

            const cloudLeft = cloudScreenX - imgW / 2 + (bounds.left / img.naturalWidth) * imgW;
            const cloudRight = cloudScreenX - imgW / 2 + (bounds.right / img.naturalWidth) * imgW;
            const cloudTop = cloudScreenY - imgH / 2 + (bounds.top / img.naturalHeight) * imgH;
            const cloudBottom = cloudScreenY - imgH / 2 + (bounds.bottom / img.naturalHeight) * imgH;

            const cloudRect = {
              x: cloudLeft,
              y: cloudTop,
              w: cloudRight - cloudLeft,
              h: cloudBottom - cloudTop
            };

            if (rectOverlap(playerRect, cloudRect) && player.vy > 0) {
              return { cloudScreenY, layerIdx: li };
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Trigger cloud bounce
   */
  triggerCloudBounce() {
    this.cloudBounceCooldown = 0.35; // align with legacy bounce timing
  }

  /**
   * Get cloud drift value
   */
  getCloudDrift() {
    return this.cloudDrift;
  }

  /**
   * Set cloud drift value
   */
  setCloudDrift(value) {
    this.cloudDrift = value;
  }

  /**
   * Get cloud camera Y
   */
  getCloudCamY() {
    return this.cloudCamY;
  }

  /**
   * Set cloud camera Y
   */
  setCloudCamY(value) {
    this.cloudCamY = value;
  }

  /**
   * Arm cloud bounce
   */
  armCloudBounce(armed) {
    this.cloudBounceArmed = armed;
  }

  /**
   * Check if cloud bounce is armed
   */
  isCloudBounceArmed() {
    return this.cloudBounceArmed;
  }

}
